import React, { useState, useEffect } from "react";
import { Modal, Input, InputNumber, message, Form } from "antd";
import PageLayout from "@components/pageLayout/PageLayout.react";
import api from "@/libs/api";
import "./vppPlans.css";

function VpFormToggle({ checked, onChange }) {
  return (
    <div className="vp-status-toggle">
      <button
        type="button"
        className={`vp-toggle-btn left ${checked ? "selected" : ""}`}
        onClick={() => onChange(true)}
      >
        Active
      </button>
      <button
        type="button"
        className={`vp-toggle-btn right ${!checked ? "selected" : ""}`}
        onClick={() => onChange(false)}
      >
        Inactive
      </button>
    </div>
  );
}

export default function VppPlans() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    setLoading(true);
    try {
      const res = await api.get("/api/vpp-plans");
      if (res.data.success) {
        setPlans(res.data.data || []);
      }
    } catch (err) {
      console.error("Failed to fetch VPP plans:", err);
      message.error("Failed to load VPP plans");
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ is_active: true, peak_bonus_percent: 0, sort_order: 100 });
    setModalOpen(true);
  };

  const openEdit = (plan) => {
    setEditing(plan);
    form.setFieldsValue({
      name: plan.name,
      description: plan.description || "",
      revenue_share_percent: parseFloat(plan.revenue_share_percent),
      min_discharge_kwh: plan.min_discharge_kwh ? parseFloat(plan.min_discharge_kwh) : null,
      max_discharge_kwh: plan.max_discharge_kwh ? parseFloat(plan.max_discharge_kwh) : null,
      peak_bonus_percent: parseFloat(plan.peak_bonus_percent) || 0,
      is_active: plan.is_active === 1 || plan.is_active === true,
      available_from: plan.available_from || "",
      available_to: plan.available_to || "",
      sort_order: plan.sort_order ?? 100,
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const payload = { ...values, is_active: values.is_active ? 1 : 0 };
      if (editing) {
        const res = await api.put(`/api/vpp-plans/${editing.id}`, payload);
        if (res.data.success) {
          message.success("VPP plan updated");
          setModalOpen(false);
          fetchPlans();
        }
      } else {
        const res = await api.post("/api/vpp-plans", payload);
        if (res.data.success) {
          message.success("VPP plan created");
          setModalOpen(false);
          fetchPlans();
        }
      }
    } catch (err) {
      if (err?.errorFields) return;
      console.error("Save failed:", err);
      message.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (plan) => {
    try {
      const payload = {
        name: plan.name,
        description: plan.description,
        revenue_share_percent: plan.revenue_share_percent,
        min_discharge_kwh: plan.min_discharge_kwh,
        max_discharge_kwh: plan.max_discharge_kwh,
        peak_bonus_percent: plan.peak_bonus_percent,
        is_active: plan.is_active ? 0 : 1,
        available_from: plan.available_from,
        available_to: plan.available_to,
        sort_order: plan.sort_order,
      };
      const res = await api.put(`/api/vpp-plans/${plan.id}`, payload);
      if (res.data.success) {
        message.success(plan.is_active ? "Plan deactivated" : "Plan activated");
        fetchPlans();
      }
    } catch (err) {
      console.error("Toggle failed:", err);
      message.error("Action failed");
    }
  };

  const fmt = (val, suffix = "") =>
    val != null && val !== "" ? `${val}${suffix}` : "—";

  return (
    <PageLayout active="vppPlans">
      <div className="vp-page">
        <div className="vp-header">
          <div>
            <h1 className="vp-title">VPP Plans</h1>
            <p className="vp-subtitle">
              Manage VPP participation plans offered to customers.
              Deactivate a plan to stop new customers from enrolling.
            </p>
          </div>
          <button className="vp-btn-add" onClick={openCreate}>
            + Add Plan
          </button>
        </div>

        <div className="vp-table-section">
          <div className="vp-table-header">
            <h2 className="vp-section-title">All Plans</h2>
            <span className="vp-count">{plans.length} plan{plans.length !== 1 ? "s" : ""}</span>
          </div>

          {loading ? (
            <div className="vp-loading">Loading...</div>
          ) : (
            <div className="vp-table-wrapper">
              <table className="vp-table">
                <thead>
                  <tr>
                    <th>Plan Name</th>
                    <th>Revenue Share</th>
                    <th>Min Discharge</th>
                    <th>Max Discharge</th>
                    <th>Peak Bonus</th>
                    <th>Sort</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {plans.map((plan) => {
                    const isActive = plan.is_active === 1 || plan.is_active === true;
                    return (
                      <tr key={plan.id} className={!isActive ? "vp-row-inactive" : ""}>
                        <td>
                          <div className="vp-plan-name">{plan.name}</div>
                          {plan.description && (
                            <div className="vp-plan-desc">{plan.description}</div>
                          )}
                        </td>
                        <td>
                          <span className="vp-highlight">{fmt(plan.revenue_share_percent, "%")}</span>
                        </td>
                        <td className="vp-td-secondary">{fmt(plan.min_discharge_kwh, " kWh")}</td>
                        <td className="vp-td-secondary">{fmt(plan.max_discharge_kwh, " kWh")}</td>
                        <td className="vp-td-secondary">{fmt(plan.peak_bonus_percent, "%")}</td>
                        <td className="vp-td-secondary">{plan.sort_order}</td>
                        <td>
                          <div className="vp-status-toggle">
                            <button
                              className={`vp-toggle-btn left ${isActive ? "selected" : ""}`}
                              onClick={() => !isActive && handleToggleActive(plan)}
                            >
                              Active
                            </button>
                            <button
                              className={`vp-toggle-btn right ${!isActive ? "selected" : ""}`}
                              onClick={() => isActive && handleToggleActive(plan)}
                            >
                              Inactive
                            </button>
                          </div>
                        </td>
                        <td>
                          <button className="vp-btn-edit" onClick={() => openEdit(plan)}>
                            Edit
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                  {plans.length === 0 && (
                    <tr>
                      <td colSpan={8} className="vp-empty">No VPP plans found</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <Modal
          title={editing ? `Edit — ${editing.name}` : "Add VPP Plan"}
          open={modalOpen}
          onOk={handleSave}
          onCancel={() => setModalOpen(false)}
          okText={editing ? "Save" : "Create"}
          confirmLoading={saving}
          width={560}
        >
          <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
            <Form.Item
              label="Plan Name"
              name="name"
              rules={[{ required: true, message: "Plan name is required" }]}
            >
              <Input placeholder="e.g. Premium Plan" />
            </Form.Item>

            <Form.Item label="Description" name="description">
              <Input.TextArea rows={2} placeholder="Optional description" />
            </Form.Item>

            <div className="vp-form-row">
              <Form.Item
                label="Revenue Share %"
                name="revenue_share_percent"
                rules={[{ required: true, message: "Required" }]}
                style={{ flex: 1 }}
              >
                <InputNumber min={0} max={100} precision={2} placeholder="e.g. 60" style={{ width: "100%" }} />
              </Form.Item>

              <Form.Item
                label="Peak Bonus %"
                name="peak_bonus_percent"
                style={{ flex: 1 }}
              >
                <InputNumber min={0} max={100} precision={2} placeholder="0" style={{ width: "100%" }} />
              </Form.Item>
            </div>

            <div className="vp-form-row">
              <Form.Item label="Min Discharge (kWh)" name="min_discharge_kwh" style={{ flex: 1 }}>
                <InputNumber min={0} precision={2} placeholder="e.g. 5" style={{ width: "100%" }} />
              </Form.Item>
              <Form.Item label="Max Discharge (kWh)" name="max_discharge_kwh" style={{ flex: 1 }}>
                <InputNumber min={0} precision={2} placeholder="No limit" style={{ width: "100%" }} />
              </Form.Item>
            </div>

            <div className="vp-form-row">
              <Form.Item label="Available From" name="available_from" style={{ flex: 1 }}>
                <Input type="date" />
              </Form.Item>
              <Form.Item label="Available To" name="available_to" style={{ flex: 1 }}>
                <Input type="date" placeholder="Leave blank = no end date" />
              </Form.Item>
            </div>

            <div className="vp-form-row">
              <Form.Item label="Sort Order" name="sort_order" style={{ flex: 1 }}>
                <InputNumber min={1} placeholder="100" style={{ width: "100%" }} />
              </Form.Item>
              <Form.Item label="Active" name="is_active" valuePropName="checked" style={{ flex: 1 }}>
                <VpFormToggle />
              </Form.Item>
            </div>
          </Form>
        </Modal>
      </div>
    </PageLayout>
  );
}
